import { cn } from "./cn";

/**
 * A single-choice button row; nothing is selected when `value` matches no option.
 *
 * Moved here from the Reports page so the dashboards' range and measure switches
 * are the same control rather than a lookalike. The selected segment is lifted
 * with a hairline ring, not a shadow: resting depth is a line (see styles.css).
 */
export function SegmentedControl<T extends string>({
  label,
  options,
  value,
  onChange,
  fit = false,
  className,
}: {
  label: string;
  options: { key: T; label: string }[];
  value: T | undefined;
  onChange: (next: T) => void;
  /**
   * Size each segment to its label instead of sharing the row equally. For a
   * control that sits in a toolbar rather than filling a form column, where
   * equal shares of an auto width truncate every label.
   */
  fit?: boolean;
  className?: string;
}) {
  return (
    <div
      role="radiogroup"
      aria-label={label}
      className={cn(
        "h-9 items-center gap-0.5 rounded-md border border-line bg-surface-sunken p-0.5",
        fit ? "inline-flex max-w-full overflow-x-auto scrollbar-thin" : "flex",
        className
      )}
    >
      {options.map((option) => {
        const active = value === option.key;
        return (
          <button
            key={option.key}
            type="button"
            role="radio"
            aria-checked={active}
            onClick={() => onChange(option.key)}
            className={cn(
              "rounded-sm py-1 text-xs transition-colors",
              fit ? "shrink-0 whitespace-nowrap px-3" : "min-w-0 flex-1 truncate px-2",
              active
                ? "bg-surface font-medium text-text ring-1 ring-line-strong"
                : "text-subtle hover:text-text"
            )}
          >
            {option.label}
          </button>
        );
      })}
    </div>
  );
}
